/*
 * promo-scheduler.js
 * --------------------------------------------------------------------------
 * Defines two custom elements used by the scheduled promo bar and popup:
 *   <promo-bar>    a thin dismissible top bar
 *   <promo-popup>  a first-visit modal
 *
 * Both contain one or more [data-promo-window] children. Each window carries
 * data-promo-start / data-promo-end (Unix epoch seconds, store-timezone based,
 * possibly empty = unbounded). The active window is the first one whose range
 * contains "now". A window with NO start and NO end is treated as inactive, so
 * an unscheduled promo never shows. Selection happens here (client-side) so
 * Shopify page caching can't pin a stale state.
 *
 * Frequency (popup, via data-frequency):
 *   once_per_promo   show once per visitor per promo window  (localStorage)
 *   once_per_session show once per browser session           (sessionStorage)
 *   every_visit      show on every page load                 (no storage)
 */
(function () {
  if (window.__promoSchedulerLoaded) return;
  window.__promoSchedulerLoaded = true;

  var nowSec = function () { return Math.floor(Date.now() / 1000); };

  function pickActiveWindow(root) {
    var windows = root.querySelectorAll('[data-promo-window]');
    var t = nowSec();
    for (var i = 0; i < windows.length; i++) {
      var el = windows[i];
      var start = parseInt(el.getAttribute('data-promo-start'), 10);
      var end = parseInt(el.getAttribute('data-promo-end'), 10);
      var hasStart = !isNaN(start);
      var hasEnd = !isNaN(end);
      // No schedule at all -> not active (won't show by default).
      if (!hasStart && !hasEnd) continue;
      var startOk = hasStart ? t >= start : true;
      var endOk = hasEnd ? t <= end : true;
      if (startOk && endOk) return el;
    }
    return null;
  }

  function windowId(prefix, el) {
    var key = el.getAttribute('data-promo-key') || 'promo';
    var stamp = el.getAttribute('data-promo-start') || el.getAttribute('data-promo-end') || '0';
    return prefix + ':' + key + ':' + stamp;
  }

  /* ---------------------------------------------------------------- bar -- */
  if (!customElements.get('promo-bar')) {
    customElements.define('promo-bar', class extends HTMLElement {
      connectedCallback() {
        var active = pickActiveWindow(this);
        if (!active) { this.remove(); return; }

        this.storageKey = windowId('promo-bar-dismissed', active);
        if (this.dataset.dismissible === 'true') {
          try { if (localStorage.getItem(this.storageKey)) { this.remove(); return; } } catch (e) {}
        }

        var windows = this.querySelectorAll('[data-promo-window]');
        for (var i = 0; i < windows.length; i++) windows[i].hidden = windows[i] !== active;
        this.hidden = false;

        var self = this;
        var closeBtn = this.querySelector('[data-promo-close]');
        if (closeBtn) {
          closeBtn.addEventListener('click', function () {
            try { localStorage.setItem(self.storageKey, '1'); } catch (e) {}
            self.remove();
          });
        }
      }
    });
  }

  /* -------------------------------------------------------------- popup -- */
  if (!customElements.get('promo-popup')) {
    customElements.define('promo-popup', class extends HTMLElement {
      connectedCallback() {
        this.active = pickActiveWindow(this);
        if (!this.active) { this.remove(); return; }

        this.frequency = this.dataset.frequency || 'once_per_promo';
        this.seenKey = windowId('promo-popup-seen', this.active);
        if (!this.shouldShow()) { this.remove(); return; }

        this.dialog = this.querySelector('[data-promo-dialog]');
        this.overlay = this.querySelector('[data-promo-overlay]');

        var windows = this.querySelectorAll('[data-promo-window]');
        for (var i = 0; i < windows.length; i++) windows[i].hidden = windows[i] !== this.active;

        var self = this;
        this.keyHandler = function (e) { if (e.key === 'Escape') self.close(); };

        this.addEventListener('click', function (e) {
          if (e.target.closest('[data-promo-close]') || e.target === self.overlay) self.close();
        });

        var delay = (parseInt(this.dataset.delay, 10) || 0) * 1000;
        this.openTimer = setTimeout(function () { self.open(); }, delay);
      }

      shouldShow() {
        try {
          if (this.frequency === 'every_visit') return true;
          if (this.frequency === 'once_per_session') return !sessionStorage.getItem(this.seenKey);
          return !localStorage.getItem(this.seenKey);
        } catch (e) { return true; }
      }

      markSeen() {
        try {
          if (this.frequency === 'once_per_session') sessionStorage.setItem(this.seenKey, '1');
          else if (this.frequency !== 'every_visit') localStorage.setItem(this.seenKey, '1');
        } catch (e) {}
      }

      open() {
        this.lastFocused = document.activeElement;
        this.hidden = false;
        // force reflow so the opening transition runs
        void this.offsetHeight;
        this.classList.add('promo-popup--open');
        document.body.classList.add('promo-popup-open');
        document.addEventListener('keydown', this.keyHandler);
        this.markSeen();

        var focusable = this.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length) focusable[0].focus();
      }

      close() {
        clearTimeout(this.openTimer);
        this.classList.remove('promo-popup--open');
        document.body.classList.remove('promo-popup-open');
        document.removeEventListener('keydown', this.keyHandler);
        var self = this;
        setTimeout(function () { self.hidden = true; }, 350);
        if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
      }
    });
  }
})();
